import supabase from './supabaseClient';

const summarizeMealScheduleBatch = (rows) => {
  const count = rows.length;
  const samples = rows.slice(0, 5).map((row) => row.summary).filter(Boolean);
  const action = rows[0]?.action || 'update';
  const noun = action === 'delete' ? 'removed from the meal calendar' : 'updated meal calendar';
  return {
    id: `meal_schedule_batch_${rows[0]?.changed_at || Date.now()}_${rows[0]?.care_home_id || 'all'}_${rows[0]?.actor_name || 'actor'}`,
    changed_at: rows[0]?.changed_at,
    entity_type: 'meal_schedule',
    action,
    summary: `${action === 'delete' ? 'Removed' : 'Updated'} meal calendar with ${count} meal${count === 1 ? '' : 's'}`,
    notes: samples.join(' | ') || null,
    actor_name: rows[0]?.actor_name || '—',
    actor_role: rows[0]?.actor_role || 'staff',
    care_home_id: rows[0]?.care_home_id || null,
    actor_care_home_id: rows[0]?.actor_care_home_id || null,
    _groupedCount: count,
  };
};

const collapseMealScheduleRows = (rows = []) => {
  const result = [];
  let batch = [];

  const flushBatch = () => {
    if (batch.length === 0) return;
    if (batch.length === 1) {
      result.push(batch[0]);
    } else {
      result.push(summarizeMealScheduleBatch(batch));
    }
    batch = [];
  };

  rows.forEach((row) => {
    const isMealScheduleBatchable = row?.entity_type === 'meal_schedule' && (row?.action === 'insert' || row?.action === 'delete');
    const batchKey = `${row?.changed_at || ''}|${row?.care_home_id || ''}|${row?.actor_name || ''}|${row?.actor_role || ''}|${row?.action || ''}`;
    const currentKey = batch[0] ? `${batch[0]?.changed_at || ''}|${batch[0]?.care_home_id || ''}|${batch[0]?.actor_name || ''}|${batch[0]?.actor_role || ''}|${batch[0]?.action || ''}` : null;

    if (isMealScheduleBatchable && (!currentKey || currentKey === batchKey)) {
      batch.push(row);
      return;
    }

    flushBatch();
    batch.push(row);
  });

  flushBatch();
  return result;
};

export async function writeAuditLog({
  careHomeId = null,
  sourceTable = null,
  entityType,
  entityId = null,
  action,
  summary,
  notes = null,
  beforeData = null,
  afterData = null,
  actorUserId = null,
  actorName = null,
  actorRole = null,
  actorCareHomeId = null,
  actorOrg = null,
  changedAt = null,
} = {}) {
  try {
    const { error } = await supabase.from('audit_logs').insert({
      care_home_id: careHomeId,
      source_table: sourceTable,
      entity_type: entityType,
      entity_id: entityId,
      action,
      summary,
      notes,
      before_data: beforeData,
      after_data: afterData,
      actor_user_id: actorUserId,
      actor_name: actorName,
      actor_role: actorRole,
      actor_care_home_id: actorCareHomeId,
      actor_org: actorOrg,
      changed_at: changedAt || new Date().toISOString(),
    });
    if (error) throw error;
    return true;
  } catch (err) {
    console.warn('writeAuditLog failed:', err?.message || err);
    return false;
  }
}

export async function listAuditLogs({
  page = 1,
  pageSize = 20,
  includeCount = false,
  careHomeId,
  careHomeIds,
  startDate,
  endDate,
  entityType,
  action,
  actorRole,
  searchSummary,
} = {}) {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('audit_logs')
    .select(
      'id, changed_at, entity_type, action, summary, notes, actor_name, actor_role, care_home_id, actor_care_home_id',
      { count: includeCount ? 'estimated' : undefined }
    )
    .order('changed_at', { ascending: false })
    .range(from, to);

  if (careHomeId) {
    query = query.eq('care_home_id', careHomeId);
  } else if (Array.isArray(careHomeIds) && careHomeIds.length > 0) {
    query = query.in('care_home_id', careHomeIds);
  }
  if (startDate) query = query.gte('changed_at', new Date(startDate).toISOString());
  if (endDate) query = query.lte('changed_at', new Date(endDate).toISOString());
  if (entityType) query = query.eq('entity_type', entityType);
  if (action) query = query.eq('action', action);
  if (actorRole) query = query.eq('actor_role', actorRole);
  if (searchSummary) query = query.ilike('summary', `%${searchSummary}%`);

  const { data, error, count } = await query;

  if (error) {
    console.error('Audit logs query error:', error);
    throw error;
  }

  const groupedData = collapseMealScheduleRows(data || []);
  return { data: groupedData, count };
}
